import isEmpty from 'lodash/isEmpty'
import {
  DirectionEnum,
  PivotAggregationEnum,
  PivotDateGroupEnum,
  PivotFieldTypeEnum,
  PivotGrandTotalsEnum,
  PivotLayoutProblemEnum,
  PivotNegativeNumberFormatEnum,
  PivotTextAlignEnum,
} from '@ztechapps/common.contract'
import type {
  PivotField,
  PivotLayout,
  PivotLayoutFilter,
  PivotLayoutMeasure,
  PivotLoadedDays,
  PivotLayoutProblem,
} from '@ztechapps/common.contract'
import { compilePivotFormula } from '../compile-pivot-formula'
import type { PivotFormulaReference } from '../compile-pivot-formula'
import { isObject } from '../is-object'
import { isPivotCalculatedMeasure } from '../is-pivot-calculated-measure'
import { listPivotFieldAggregations } from '../list-pivot-field-aggregations'
import { isCalendarDay } from '../is-calendar-day'
import { findPivotValueFilterProblem } from '../find-pivot-value-filter-problem'
import { findPivotValueComparisonProblem } from '../find-pivot-value-comparison-problem'

/** Arguments for validating a proposed pivot layout against the open report's fields. */
type ValidatePivotLayoutArgs = { layout: PivotLayout; fields: PivotField[]; loadedDays: PivotLoadedDays[] }

const fieldByName = (fields: PivotField[]): Map<string, PivotField> =>
  new Map(fields.map((field) => [field.name, field]))

/**
 * Checks a proposed pivot layout against the open report's fields and loaded days.
 * Returns one problem per failed rule, never throwing — a rejected layout comes
 * back to the model as text so it can correct the layout or explain why it cannot.
 */
export const validatePivotLayout = ({ layout, fields, loadedDays }: ValidatePivotLayoutArgs): PivotLayoutProblem[] => {
  const problems: PivotLayoutProblem[] = []
  const invalid = (message: string): void => {
    problems.push({ code: PivotLayoutProblemEnum.Invalid, message })
  }
  if (!layout || typeof layout !== 'object') {
    invalid('The layout must be an object.')
    return problems
  }
  const known = fieldByName(fields)

  // a required list key holding anything but a list is one problem — its zone is skipped, not read
  const readList = <T>(key: 'rows' | 'columns' | 'measures' | 'filters' | 'memberSorts' | 'columnWidths'): T[] => {
    const value: unknown = (layout as unknown as Record<string, unknown>)[key]
    if (!Array.isArray(value)) {
      invalid(`The layout's ${key} must be a list.`)
      return []
    }
    return value as T[]
  }

  const checkDateGroup = (field: string, dateGroup: unknown): void => {
    if (dateGroup === undefined) return
    if (!Object.values(PivotDateGroupEnum).includes(dateGroup as PivotDateGroupEnum)) {
      invalid(`${field} groups by ${String(dateGroup)}, which is not a Year, Quarter or Month grouping.`)
      return
    }
    if (known.get(field)?.type !== PivotFieldTypeEnum.Date)
      invalid(`${field} is not a date field — only a date field takes a date grouping.`)
  }

  const rows = readList<{ field: string; dateGroup?: PivotDateGroupEnum }>('rows')
  const columns = readList<{ field: string; dateGroup?: PivotDateGroupEnum }>('columns')
  const filters = readList<PivotLayoutFilter>('filters')
  const memberSorts = readList<{ field: string; dateGroup?: PivotDateGroupEnum; direction: DirectionEnum }>(
    'memberSorts'
  )
  readList<unknown>('columnWidths')

  // without a date-typed field no range can ever validate — report that once, not a cascade per filter
  if (
    filters.some((item) => item.dateRange !== undefined) &&
    !fields.some((field) => field.type === PivotFieldTypeEnum.Date)
  ) {
    problems.push({
      code: PivotLayoutProblemEnum.NoDateField,
      message: 'This report has no date field — a date range cannot be set.',
    })
  }

  rows.forEach((item) => {
    if (!isObject(item)) {
      invalid('Rows holds an entry that is not an object.')
      return
    }
    if (!known.has(item.field)) invalid(`Rows names ${item.field}, which is not a field of this report.`)
    else checkDateGroup(item.field, item.dateGroup)
  })
  columns.forEach((item) => {
    if (!isObject(item)) {
      invalid('Columns holds an entry that is not an object.')
      return
    }
    if (!known.has(item.field)) invalid(`Columns names ${item.field}, which is not a field of this report.`)
    else checkDateGroup(item.field, item.dateGroup)
  })
  filters.forEach((item) => {
    if (!isObject(item)) {
      invalid('Filters holds an entry that is not an object.')
      return
    }
    if (!known.has(item.field)) invalid(`Filters names ${item.field}, which is not a field of this report.`)
    else {
      checkDateGroup(item.field, item.dateGroup)
      if (
        item.conditionFilter !== undefined &&
        (!isEmpty(item.includedValues) || !isEmpty(item.excludedValues) || item.dateRange !== undefined)
      )
        invalid(
          `${item.field} holds a Condition filter beside a member selection or date range — a field takes only one filter kind.`
        )
      if (item.conditionFilter !== undefined && item.dateGroup !== undefined)
        invalid(`${item.field} groups by ${String(item.dateGroup)} — a Condition filter needs an ungrouped date field.`)
      const hasValueKind = item.valueFilter !== undefined || item.valueComparison !== undefined
      if (item.conditionFilter !== undefined && hasValueKind)
        invalid(`${item.field} holds a Condition filter beside a Value filter — a field takes only one filter kind.`)
      if (
        item.valueComparison !== undefined &&
        (!isEmpty(item.includedValues) ||
          !isEmpty(item.excludedValues) ||
          item.dateRange !== undefined ||
          item.valueFilter !== undefined)
      )
        invalid(
          `${item.field} holds a Value comparison beside a member selection, date range or Top / Bottom N — a field takes only one filter kind.`
        )
      const pivotLayout = { rows, columns, measures: Array.isArray(layout.measures) ? layout.measures : [] }
      const valueFilterProblem = findPivotValueFilterProblem(pivotLayout, item)
      if (valueFilterProblem !== undefined) invalid(valueFilterProblem)
      const valueComparisonProblem = findPivotValueComparisonProblem(pivotLayout, item)
      if (valueComparisonProblem !== undefined) invalid(valueComparisonProblem)
    }
  })
  memberSorts.forEach((item) => {
    if (!isObject(item)) {
      invalid('Member sorts holds an entry that is not an object.')
      return
    }
    if (!known.has(item.field)) invalid(`Member sorts name ${item.field}, which is not a field of this report.`)
    else {
      checkDateGroup(item.field, item.dateGroup)
      if (!Object.values(DirectionEnum).includes(item.direction))
        invalid(
          `The member sort on ${item.field} sorts ${String(item.direction)}, which is not an asc or desc direction.`
        )
    }
  })

  const measures = readList<PivotLayoutMeasure>('measures')
  const measureNames = new Set<string>()
  measures.forEach((measure) => {
    if (!isObject(measure)) {
      invalid('Values holds an entry that is not an object.')
      return
    }
    if (isPivotCalculatedMeasure(measure)) {
      if (known.has(measure.field)) invalid(`Calculated value ${measure.field} clashes with a field of this report.`)
      else if (measureNames.has(measure.field))
        invalid(`Calculated value ${measure.field} is used by another value of this layout.`)
      else {
        let references: PivotFormulaReference[]
        try {
          references = compilePivotFormula(measure.formula).references
        } catch (error) {
          invalid(`${measure.field} has an invalid formula: ${error instanceof Error ? error.message : String(error)}`)
          return
        }
        references.forEach((reference) => {
          const target = known.get(reference.field)
          if (!target) invalid(`${measure.field} aggregates ${reference.field}, which is not a field of this report.`)
          else if (!listPivotFieldAggregations(target.type).includes(reference.aggregation))
            invalid(`${reference.field} is not a number field — it allows Count or DistinctCount only.`)
        })
      }
      measureNames.add(measure.field)
      return
    }
    if (!known.has(measure.field)) {
      invalid(`Values names ${measure.field}, which is not a field of this report.`)
      measureNames.add(measure.field)
      return
    }
    if (!Object.values(PivotAggregationEnum).includes(measure.aggregation))
      invalid(`${measure.field} aggregates by ${String(measure.aggregation)}, which is not a known aggregation.`)
    else if (
      !listPivotFieldAggregations(known.get(measure.field)?.type as PivotFieldTypeEnum).includes(measure.aggregation)
    )
      invalid(`${measure.field} is not a number field — it allows Count or DistinctCount only.`)
    if (
      measure.format?.negativeNumberFormat !== undefined &&
      !Object.values(PivotNegativeNumberFormatEnum).includes(measure.format.negativeNumberFormat)
    )
      invalid(
        `${measure.field} formats negatives as ${String(measure.format.negativeNumberFormat)}, which is not a -1 or (1) format.`
      )
    if (
      measure.format?.textAlign !== undefined &&
      !Object.values(PivotTextAlignEnum).includes(measure.format.textAlign)
    )
      invalid(
        `${measure.field} aligns ${String(measure.format.textAlign)}, which is not a Left, Center or Right alignment.`
      )
    measureNames.add(measure.field)
  })

  if (layout.valueSort !== undefined) {
    if (!isObject(layout.valueSort) || !isObject(layout.valueSort.measure))
      invalid('The value sort must name a value of this layout.')
    else {
      const { measure: sorted, direction } = layout.valueSort as {
        measure: { field: string; aggregation?: PivotAggregationEnum }
        direction: DirectionEnum
      }
      const matches = (candidate: PivotLayoutMeasure): boolean =>
        isObject(candidate) &&
        candidate.field === sorted.field &&
        (sorted.aggregation === undefined ||
          (!isPivotCalculatedMeasure(candidate) && candidate.aggregation === sorted.aggregation))
      if (!measures.some(matches)) invalid(`The value sort names ${sorted.field}, which is not a value of this layout.`)
      if (!Object.values(DirectionEnum).includes(direction))
        invalid(`The value sort sorts ${String(direction)}, which is not an asc or desc direction.`)
    }
  }
  if (!Object.values(PivotGrandTotalsEnum).includes(layout.showGrandTotals))
    invalid(`Grand totals ${String(layout.showGrandTotals)} is not an Off, On, Rows or Columns mode.`)

  // a range is validated only on a known field — an unknown one already failed above
  filters.forEach((item) => {
    if (!isObject(item) || item.dateRange === undefined || !known.has(item.field as string)) return
    if (!isObject(item.dateRange)) {
      invalid(`${String(item.field)} ranges ${String(item.dateRange)}, which is not a YYYY-MM-DD day pair.`)
      return
    }
    const { from, to } = item.dateRange as { from: string; to: string }
    if (known.get(item.field)?.type !== PivotFieldTypeEnum.Date) {
      invalid(`${item.field} is not a date field — a date range needs a date field.`)
      return
    }
    if (item.dateGroup !== undefined) {
      invalid(`${item.field} groups by ${String(item.dateGroup)} — a date range needs an ungrouped date field.`)
      return
    }
    if (!isCalendarDay(from) || !isCalendarDay(to)) {
      invalid(`${item.field} ranges ${from} to ${to}, which is not a YYYY-MM-DD day pair.`)
      return
    }
    if (from > to) {
      invalid(`${item.field} ranges ${from} to ${to} — the first day falls after the last.`)
      return
    }
    const span = loadedDays.find((entry) => entry.field === item.field)
    if (span === undefined)
      problems.push({
        code: PivotLayoutProblemEnum.OutsideLoadedDays,
        message: `${item.field} ranges ${from} to ${to}, but no loaded row has a date.`,
      })
    else if (to < span.from || from > span.to)
      problems.push({
        code: PivotLayoutProblemEnum.OutsideLoadedDays,
        message: `${item.field} ranges ${from} to ${to}, outside the loaded days ${span.from} to ${span.to}.`,
      })
  })

  return problems
}
